import { Link as RouterLink } from 'react-router-dom';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// project imports
import { useTranslation } from 'contexts/LanguageContext';

import loginHero from 'assets/images/auth/login-hero.jpg';

function isFinderCell(row: number, col: number, startRow: number, startCol: number) {
  const r = row - startRow;
  const c = col - startCol;

  if (r < 0 || r > 6 || c < 0 || c > 6) return false;
  return r === 0 || r === 6 || c === 0 || c === 6 || (r >= 2 && r <= 4 && c >= 2 && c <= 4);
}

function QrPreview() {
  const size = 29;
  const cells = Array.from({ length: size * size }, (_, index) => {
    const row = Math.floor(index / size);
    const col = index % size;
    const finder =
      isFinderCell(row, col, 1, 1) || isFinderCell(row, col, 1, size - 8) || isFinderCell(row, col, size - 8, 1);
    const data = (row * 11 + col * 7 + row * col + (row % 3) * 5 + (col % 4) * 3) % 9;

    return finder || data < 4;
  });

  return (
    <Box
      aria-label="Authenticator QR code"
      sx={{
        width: { xs: 220, sm: 260 },
        aspectRatio: '1 / 1',
        display: 'grid',
        gridTemplateColumns: `repeat(${size}, 1fr)`,
        bgcolor: 'background.paper',
        border: '10px solid',
        borderColor: 'background.paper',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.08)'
      }}
    >
      {cells.map((filled, index) => (
        <Box key={index} sx={{ bgcolor: filled ? 'common.black' : 'common.white' }} />
      ))}
    </Box>
  );
}

export default function TwoFactorPage() {
  const { t } = useTranslation();

  return (
    <Grid
      container
      spacing={0}
      sx={{
        width: '100%',
        minHeight: { xs: 'auto', md: '100%' },
        height: { md: '100%' },
        alignContent: { xs: 'flex-start', md: 'stretch' },
        overflow: { xs: 'visible', md: 'hidden' }
      }}
    >
      <Grid
        size={{ xs: 12, md: 5, lg: 4 }}
        sx={{
          order: { xs: 2, md: 1 },
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          px: { xs: 2, sm: 4, md: 6, lg: 8 },
          py: { xs: 2, md: 4 },
          minHeight: { xs: 'auto', md: 0 },
          overflow: { xs: 'visible', md: 'auto' }
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: { xs: 3, sm: 4 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper'
          }}
        >
          <Stack spacing={2.5}>
            <Stack spacing={0.75}>
              <Typography variant="h3" sx={{ color: 'primary.dark' }}>
                {t('2FA Activate')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                {t('Google Authenticator verification is required for your account.')}
              </Typography>
            </Stack>

            <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 500 }}>
                {t('Secret code')}: G5RDGOLFMQZTKOBW
              </Typography>
              <QrPreview />
            </Stack>

            <Stack component="ol" spacing={0.75} sx={{ pl: 2.5, m: 0, color: 'primary.main' }}>
              <Typography component="li" variant="body1">
                {t('Install the Google Authenticator app on your mobile device.')}
              </Typography>
              <Typography component="li" variant="body1">
                {t('Scan the QR code with the Authenticator app.')}
              </Typography>
              <Typography component="li" variant="body1">
                {t('Keep a safe copy of the 16-digit secret code.')}
              </Typography>
              <Typography component="li" variant="body1">
                {t('Never share this secret code or QR code with anyone.')}
              </Typography>
            </Stack>

            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {t('Enter the authentication code supplied by your Authenticator app.')}
            </Typography>

            <FormControl fullWidth>
              <InputLabel htmlFor="otp">OTP</InputLabel>
              <OutlinedInput id="otp" label="OTP" fullWidth />
            </FormControl>

            <Button
              component={RouterLink}
              to="/hotels/details"
              fullWidth
              size="large"
              variant="contained"
            >
              {t('Submit')}
            </Button>
          </Stack>
        </Paper>
      </Grid>

      <Grid
        size={{ xs: 12, md: 7, lg: 8 }}
        sx={{
          order: { xs: 1, md: 2 },
          display: 'block',
          height: { xs: 180, sm: 240, md: '100%' },
          minHeight: { xs: 180, sm: 240, md: 0 }
        }}
      >
        <Box
          component="img"
          src={loginHero}
          alt="Business planning workspace"
          sx={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'cover',
            objectPosition: { xs: 'center 42%', md: 'center' },
            borderRadius: 0
          }}
        />
      </Grid>
    </Grid>
  );
}
